import * as React from 'react'
import type { Editor } from '@tiptap/react'
import type { FormatAction } from '../../types'
import type { toggleVariants } from '@/components/ui/toggle'
import type { VariantProps } from 'class-variance-authority'
import { CaretDownIcon, CodeIcon, DividerHorizontalIcon, LinkBreak2Icon, PlusIcon, QuoteIcon } from '@radix-ui/react-icons'
import { LinkEditPopover } from '../link/link-edit-popover'
import { ToolbarButton } from '../toolbar-button'
// import { ImageEditDialog } from '../image/image-edit-dialog'
import { ToolbarSection } from '../toolbar-section'

// Function to strip all links from the document
const stripAllLinks = (editor: Editor) => {
  const { tr, doc } = editor.state
  let modified = false

  // Traverse the entire document and collect link mark ranges
  const linkRanges: { from: number; to: number }[] = []
  
  doc.descendants((node, pos) => {
    if (node.marks) {
      node.marks.forEach(mark => {
        if (mark.type.name === 'link') {
          linkRanges.push({
            from: pos,
            to: pos + node.nodeSize
          })
        }
      })
    }
  })

  // Remove link marks in reverse order to avoid position shifts
  linkRanges.reverse().forEach(range => {
    tr.removeMark(range.from, range.to, editor.schema.marks.link)
    modified = true
  })

  if (modified) {
    editor.view.dispatch(tr)
  }
}

// Check if document has any links
const hasLinksInDocument = (editor: Editor): boolean => {
  let hasLinks = false
  editor.state.doc.descendants((node) => {
    if (node.marks && node.marks.some(mark => mark.type.name === 'link')) {
      hasLinks = true
      return false // Stop traversal once we find a link
    }
  })
  return hasLinks
}

type InsertElementAction = 'codeBlock' | 'blockquote' | 'horizontalRule'
interface InsertElement extends FormatAction {
  value: InsertElementAction
}

const formatActions: InsertElement[] = [
  {
    value: 'codeBlock',
    label: 'Code block',
    icon: <CodeIcon className="size-5" />,
    action: editor => editor.chain().focus().toggleCodeBlock().run(),
    isActive: editor => editor.isActive('codeBlock'),
    canExecute: editor => editor.can().chain().focus().toggleCodeBlock().run(),
    shortcuts: ['mod', 'alt', 'C']
  },
  {
    value: 'blockquote',
    label: 'Blockquote',
    icon: <QuoteIcon className="size-5" />,
    action: editor => editor.chain().focus().toggleBlockquote().run(),
    isActive: editor => editor.isActive('blockquote'),
    canExecute: editor => editor.can().chain().focus().toggleBlockquote().run(),
    shortcuts: ['mod', 'shift', 'B']
  },
  {
    value: 'horizontalRule',
    label: 'Divider',
    icon: <DividerHorizontalIcon className="size-5" />,
    action: editor => editor.chain().focus().setHorizontalRule().run(),
    isActive: () => false,
    canExecute: editor => editor.can().chain().focus().setHorizontalRule().run(),
    shortcuts: ['mod', 'alt', '-']
  }
]

interface SectionFiveProps extends VariantProps<typeof toggleVariants> {
  editor: Editor
  activeActions?: InsertElementAction[]
  mainActionCount?: number
}

export const SectionFive: React.FC<SectionFiveProps> = ({
  editor,
  activeActions = formatActions.map(action => action.value),
  mainActionCount = 0,
  size,
  variant
}) => {
  // Add keyboard shortcut for strip links
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'K') {
        event.preventDefault()
        if (hasLinksInDocument(editor)) {
          stripAllLinks(editor)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [editor])

  return (
    <>
      <LinkEditPopover editor={editor} size={size} variant={variant} />
      <ToolbarButton
        onClick={() => stripAllLinks(editor)}
        disabled={!hasLinksInDocument(editor)}
        tooltip="Strip all links (⌘⇧K)"
        size={size}
        variant={variant}
      >
        <LinkBreak2Icon className="size-5" />
      </ToolbarButton>
      {/* <ImageEditDialog editor={editor} size={size} variant={variant} /> */}
      <ToolbarSection
        editor={editor}
        actions={formatActions}
        activeActions={activeActions}
        mainActionCount={mainActionCount}
        dropdownIcon={
          <>
            <PlusIcon className="size-5" />
            <CaretDownIcon className="size-5" />
          </>
        }
        dropdownTooltip="Insert elements"
        size={size}
        variant={variant}
      />
    </>
  )
}

SectionFive.displayName = 'SectionFive'

export default SectionFive
